import { LockOutlined } from '@ant-design/icons'
import { attachStripePayment, clientSignup, subscribeStripeProduct } from '../../api'
import { normalizeSignupPlatformId, toDatabaseName } from '../utils'
import PaymentStep from './PaymentStep'
import AdvicePrepOrderSummary from '../adviceprep/AdvicePrepOrderSummary'
import { subscribeAdvicePrep } from '@/features/adviceprep/api'
import { advicePrepDueToday, formatAdvicePrepCurrency } from '@/features/adviceprep/pricing'
import { useSignup } from '../SignupContext'

type PaymentStepWrapperProps = {
  stripePk: string
}

export default function PaymentStepWrapper({ stripePk }: PaymentStepWrapperProps) {
  const {
    loading,
    skipPayment,
    setSkipPayment,
    isBasSignup,
    isAdvicePrepSignup,
    advicePrepStripeCustomerId,
    customerForm,
    setCustomerForm,
    setPlatformId,
    setStep,
    pricingForm,
    productSelection,
    clientSignupCompleted,
    setClientSignupCompleted,
    runAction,
    createAccountWithoutPayment,
    ensureActiveAndAuthenticated,
    hideBackButton,
    couponValid,
    advicePrepCoupon,
    setError,
  } = useSignup()

  const handleBack = () => setStep('pricing')

  const handlePaymentNext = async (paymentId: string | null) => {
    await runAction(async () => {
      if (!customerForm || !productSelection?.product) throw new Error('Missing signup details')

      if (skipPayment && isBasSignup) {
        if (!clientSignupCompleted) {
          await createAccountWithoutPayment(customerForm, false)
        }
        setStep('importData')
        return
      }

      if (!paymentId) throw new Error('Payment details are required')

      // Step 2 already created the client, the user and the Cognito login for
      // AdvicePrep (D-5, D-13), so this step only attaches the card and
      // subscribes. No clientSignup, no markActive.
      if (isAdvicePrepSignup) {
        const advicePrepAttach = await attachStripePayment({
          PaymentId: paymentId,
          CustomerId: customerForm.clientId || '',
          requireSubscription: true,
        })
        if (!advicePrepAttach?.isSuccessful) {
          throw new Error('The payment could not be processed. Please check your payment information and try again.')
        }

        // A declined card resolves with isSuccessful false rather than
        // throwing — Stripe returns an incomplete subscription, not an error.
        // Without this check the customer reaches the success screen with an
        // account and no subscription, and lands paywalled on next login.
        const subscribeResult = await subscribeAdvicePrep({
          clientId: customerForm.clientId || '',
          customerId: advicePrepAttach.paymentMethod?.customer || advicePrepAttach.customer || advicePrepStripeCustomerId,
          paymentMethodId: advicePrepAttach.paymentMethod?.id || advicePrepAttach.id || paymentId,
          promoCode: pricingForm.couponCode,
        })
        if (!subscribeResult.isSuccessful) {
          throw new Error(subscribeResult.error)
        }

        setStep('success')
        return
      }

      const signupPayload = {
        clientForm: {
          ...customerForm,
          databaseName: toDatabaseName(customerForm.companyName),
          requireSubscription: true,
          subscribeToBAS: isBasSignup,
        },
        platformType: productSelection.product,
        nocc: false,
      }

      let resolvedCustomer = customerForm
      if (!clientSignupCompleted) {
        const signupResult = await clientSignup(signupPayload)
        if (signupResult.error || !signupResult.client?.clientId) {
          throw new Error(signupResult.error || 'Unable to create account')
        }
        resolvedCustomer = {
          ...customerForm,
          clientId: signupResult.client.clientId,
          userId: signupResult.userId,
        }
        setCustomerForm(resolvedCustomer)
        setPlatformId(normalizeSignupPlatformId(signupResult.platformId))
        setClientSignupCompleted(true)
        await ensureActiveAndAuthenticated(customerForm.email, customerForm.password)
      }

      const stripeAttach = await attachStripePayment({
        PaymentId: paymentId,
        CustomerId: resolvedCustomer.clientId || '',
        requireSubscription: true,
        subscribeToBAS: isBasSignup,
      })

      if (!stripeAttach?.isSuccessful) {
        throw new Error('The payment could not be processed. Please check your payment information and try again.')
      }

      await subscribeStripeProduct({
        productSubscription: {
          CustomerID: stripeAttach.paymentMethod?.customer || stripeAttach.customer || '',
          PromoCode: pricingForm.couponCode,
          Users: pricingForm.activeUsers,
          PaymentMethodId: stripeAttach.paymentMethod?.id || stripeAttach.id || paymentId,
          CustomNumOfUsers: pricingForm.customUsers,
        },
        platformType: productSelection.product,
        requireSubscription: true,
        subscribeToBAS: isBasSignup,
      })

      setStep('importData')
    }, false)
  }

  const dueToday = advicePrepDueToday(couponValid, advicePrepCoupon)

  return (
    <PaymentStep
      loading={loading}
      skipPayment={skipPayment}
      setSkipPayment={setSkipPayment}
      isBasSignup={isBasSignup}
      hideBackButton={hideBackButton}
      onBack={handleBack}
      onSubmit={handlePaymentNext}
      clearError={() => setError(null)}
      stripePk={stripePk}
      summary={isAdvicePrepSignup ? <AdvicePrepOrderSummary /> : undefined}
      submitLabel={isAdvicePrepSignup ? `Pay ${formatAdvicePrepCurrency(dueToday, 2)} & continue` : undefined}
      footer={
        isAdvicePrepSignup ? (
          <div className="flex items-center justify-center gap-[0.375rem] text-[0.71875rem] text-gray-400">
            <LockOutlined />
            Secured by Stripe · PCI-DSS compliant · we never store card details
          </div>
        ) : undefined
      }
    />
  )
}
